<div>
  <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
    <!-- House Subtype -->
    @if (propertyDefinition().propertyType === PropertyType.HOUSE) {
      <mat-form-field appearance="outline" class="md:col-span-2" subscriptSizing="dynamic">
        <mat-label>Type de maison</mat-label>
        <mat-select [formField]="formTree.subtype" (selectionChange)="onSubtypeChange($event.value)">
          @for (type of houseSubTypes; track type) {
            <mat-option [value]="type">{{ type | etxeEnumDisplay: 'house' }}</mat-option>
          }
        </mat-select>
        @if (formTree.subtype().errors().length) {
          <mat-error>Le type de maison est requis</mat-error>
        }
      </mat-form-field>
    }

    <!-- Apartment Subtype -->
    @if (propertyDefinition().propertyType === PropertyType.APARTMENT) {
      <mat-form-field appearance="outline" class="md:col-span-2" subscriptSizing="dynamic">
        <mat-label>Type d'appartement</mat-label>
        <mat-select [formField]="formTree.subtype" (selectionChange)="onSubtypeChange($event.value)">
          <mat-select-trigger>
            @if (model().subtype; as selected) {
              {{ selected | etxeEnumDisplay: 'apartment' }}
            }
          </mat-select-trigger>
          @for (type of apartmentSubTypes; track type) {
            <mat-option #opt [value]="type" class="!h-auto !py-2.5">
              <div class="flex flex-col gap-0.5">
                <span
                  class="text-sm font-medium transition-colors"
                  [class]="opt.selected ? 'text-white' : 'text-neutral-12'"
                >
                  {{ type | etxeEnumDisplay: 'apartment' }}
                </span>
                <span
                  class="text-xs leading-snug transition-colors"
                  [class]="opt.selected ? 'text-white/80' : 'text-neutral-10'"
                >
                  {{ getApartmentDescription(type) }}
                </span>
              </div>
            </mat-option>
          }
        </mat-select>
        @if (formTree.subtype().errors().length) {
          <mat-error>Le type d'appartement est requis</mat-error>
        }
      </mat-form-field>
    }

    <!-- Commercial Subtype -->
    @if (propertyDefinition().propertyType === PropertyType.BUSINESS) {
      <mat-form-field appearance="outline" class="md:col-span-2" subscriptSizing="dynamic">
        <mat-label>Type de commerce</mat-label>
        <mat-select [formField]="formTree.subtype" (selectionChange)="onSubtypeChange($event.value)">
          @for (type of commercialSubTypes; track type) {
            <mat-option [value]="type">{{ type | etxeEnumDisplay: 'commercial' }}</mat-option>
          }
        </mat-select>
        @if (formTree.subtype().errors().length) {
          <mat-error>Le type de commerce est requis</mat-error>
        }
      </mat-form-field>
    }

    <!-- Land Subtype -->
    @if (propertyDefinition().propertyType === PropertyType.LAND) {
      <mat-form-field appearance="outline" class="md:col-span-2" subscriptSizing="dynamic">
        <mat-label>Type de terrain</mat-label>
        <mat-select [formField]="formTree.subtype" (selectionChange)="onSubtypeChange($event.value)">
          @for (type of landSubTypes; track type) {
            <mat-option [value]="type">{{ type | etxeEnumDisplay: 'land' }}</mat-option>
          }
        </mat-select>
        @if (formTree.subtype().errors().length) {
          <mat-error>Le type de terrain est requis</mat-error>
        }
      </mat-form-field>
    }

    <!-- Parking Subtype -->
    @if (propertyDefinition().propertyType === PropertyType.PARKING_BOX) {
      <mat-form-field appearance="outline" class="md:col-span-2" subscriptSizing="dynamic">
        <mat-label>Type de stationnement</mat-label>
        <mat-select [formField]="formTree.subtype" (selectionChange)="onSubtypeChange($event.value)">
          @for (type of parkingTypes; track type) {
            <mat-option [value]="type">{{ type | etxeEnumDisplay: 'parking' }}</mat-option>
          }
        </mat-select>
        @if (formTree.subtype().errors().length) {
          <mat-error>Le type de stationnement est requis</mat-error>
        }
      </mat-form-field>
    }

    <!-- Professional Subtype -->
    @if (propertyDefinition().propertyType === PropertyType.PROFESSIONAL) {
      <mat-form-field appearance="outline" class="md:col-span-2" subscriptSizing="dynamic">
        <mat-label>Type de local</mat-label>
        <mat-select [formField]="formTree.subtype" (selectionChange)="onSubtypeChange($event.value)">
          @for (type of professionalSubTypes; track type) {
            <mat-option [value]="type">{{ type | etxeEnumDisplay: 'professional' }}</mat-option>
          }
        </mat-select>
        @if (formTree.subtype().errors().length) {
          <mat-error>Le type de local est requis</mat-error>
        }
      </mat-form-field>
    }
  </div>

  <div class="mt-6 flex flex-col gap-6">
    <div class="flex flex-col gap-6">
      <!-- Configuration des étages (données BDNB) -->
      @if (
        propertyDefinition().propertyType !== PropertyType.APARTMENT &&
        propertyDefinition().propertyType !== PropertyType.LAND
      ) {
        <div class="flex flex-col gap-3 rounded-2xl border border-neutral-3 bg-neutral-2/40 p-5">
          <div class="flex items-center justify-between">
            <div class="flex flex-col gap-0.5">
              <span class="text-sm font-medium text-neutral-12">
                {{
                  propertyDefinition().propertyType === PropertyType.HOUSE
                    ? 'Configuration des étages'
                    : 'Nombre de niveaux du bâtiment'
                }}
              </span>
              <span class="text-xs text-neutral-10"
                >Renseigné automatiquement via le registre national des bâtiments (BDNB)</span
              >
            </div>
            <div>
              @if (model().accessibility.buildingNumberOfFloors !== null) {
                @if (propertyDefinition().propertyType === PropertyType.HOUSE) {
                  @if (
                    (model().accessibility.apartment.numberOfFloors ?? 0) === 0 ||
                    model().accessibility.buildingNumberOfFloors === 1
                  ) {
                    <span
                      class="inline-flex items-center gap-1.5 rounded-full border border-neutral-4 bg-neutral-3 px-3 py-1 text-xs font-semibold text-neutral-12"
                    >
                      Maison de plain-pied
                    </span>
                  } @else {
                    <span
                      class="inline-flex items-center gap-1.5 rounded-full border border-neutral-4 bg-neutral-3 px-3 py-1 text-xs font-semibold text-neutral-12"
                    >
                      Maison à étages
                    </span>
                  }
                } @else {
                  <span
                    class="inline-flex items-center rounded-lg border border-neutral-4 bg-white px-3 py-1 text-sm font-semibold text-neutral-12 shadow-xs dark:bg-neutral-3"
                  >
                    {{ model().accessibility.buildingNumberOfFloors }}
                    {{ model().accessibility.buildingNumberOfFloors! > 1 ? 'niveaux' : 'niveau' }}
                  </span>
                }
              } @else {
                <span class="text-xs italic text-neutral-10">Non renseigné</span>
              }
            </div>
          </div>

          @if (
            propertyDefinition().propertyType === PropertyType.HOUSE &&
            model().accessibility.buildingNumberOfFloors !== null &&
            (model().accessibility.apartment.numberOfFloors ?? 0) > 0
          ) {
            <div class="flex items-center justify-between border-t border-neutral-3 pt-3">
              <div class="flex flex-col gap-0.5">
                <span class="text-sm font-medium text-neutral-11">Nombre d'étages</span>
                <span class="text-xs text-neutral-9">Étages au-dessus du rez-de-chaussée</span>
              </div>
              <span
                class="inline-flex items-center rounded-lg border border-neutral-4 bg-white px-3 py-1 text-sm font-semibold text-neutral-12 shadow-xs dark:bg-neutral-3"
              >
                {{ model().accessibility.apartment.numberOfFloors }}
                {{ model().accessibility.apartment.numberOfFloors! > 1 ? 'étages' : 'étage' }}
              </span>
            </div>
          }
        </div>
      }

      @if (propertyDefinition().propertyType === PropertyType.APARTMENT) {
        <!-- Specific Floor Info -->
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2 items-start">
          <aura-number-input
            label="À quel étage se trouve votre appartement ?"
            [value]="model().accessibility.apartment.floor ?? undefined"
            (valueChange)="
              model.update((m) => ({
                ...m,
                accessibility: {
                  ...m.accessibility,
                  apartment: { ...m.accessibility.apartment, floor: $event ?? null },
                },
              }))
            "
            placeholder="0"
            [min]="0"
            [max]="model().accessibility.buildingNumberOfFloors ?? 163"
          ></aura-number-input>

          <!-- Nombre total d'étages dans l'immeuble (données BDNB) -->
          <div class="flex flex-col justify-between rounded-xl border border-neutral-3 bg-neutral-2/40 p-4">
            <div class="flex flex-col gap-0.5">
              <span class="text-sm font-medium text-neutral-12">Nombre total d'étages dans l'immeuble</span>
              <span class="text-xs text-neutral-10"
                >Renseigné automatiquement via le registre national des bâtiments (BDNB)</span
              >
            </div>
            <div class="mt-3">
              @if (model().accessibility.buildingNumberOfFloors !== null) {
                <span
                  class="inline-flex items-center rounded-lg border border-neutral-4 bg-white px-3 py-1 text-sm font-semibold text-neutral-12 shadow-xs dark:bg-neutral-3"
                >
                  {{ model().accessibility.buildingNumberOfFloors }}
                  {{ model().accessibility.buildingNumberOfFloors! > 1 ? 'étages' : 'étage' }}
                </span>
              } @else {
                <span class="text-xs italic text-neutral-10">Non renseigné</span>
              }
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <!-- Carte ascenseur -->
          <button
            type="button"
            (click)="
              model.update((m) => ({
                ...m,
                accessibility: {
                  ...m.accessibility,
                  buildingHasElevator: !m.accessibility.buildingHasElevator,
                },
              }))
            "
            class="group flex cursor-pointer items-center justify-between rounded-2xl border p-4 text-left transition-all duration-200 select-none"
            [class]="
              model().accessibility.buildingHasElevator
                ? 'border-indigo-8 bg-indigo-a2 dark:bg-indigo-950/40 ring-2 ring-indigo-7/20 shadow-xs'
                : 'border-neutral-4 bg-neutral-1/90 hover:border-indigo-6/50 hover:bg-neutral-2/60'
            "
          >
            <div class="flex items-center gap-3.5">
              <div
                class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-all duration-200"
                [class]="
                  model().accessibility.buildingHasElevator
                    ? 'bg-indigo-9 text-white shadow-sm shadow-indigo-9/30'
                    : 'bg-indigo-a3 text-indigo-11 border border-indigo-a4 group-hover:bg-indigo-a4 group-hover:text-indigo-12'
                "
              >
                <svg
                  class="h-6 w-6 shrink-0 transition-colors duration-200"
                  [class]="
                    model().accessibility.buildingHasElevator
                      ? 'text-white'
                      : 'text-indigo-11 group-hover:text-indigo-12'
                  "
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect x="3.5" y="2.5" width="17" height="19" rx="2.5" />
                  <polyline points="6.5 9.5 9 7 11.5 9.5" />
                  <line x1="9" y1="7" x2="9" y2="17" />
                  <polyline points="12.5 14.5 15 17 17.5 14.5" />
                  <line x1="15" y1="7" x2="15" y2="17" />
                </svg>
              </div>
              <div class="flex flex-col gap-0.5">
                <span
                  class="text-sm font-semibold transition-colors duration-200"
                  [class]="
                    model().accessibility.buildingHasElevator
                      ? 'text-indigo-12 dark:text-white'
                      : 'text-neutral-12 group-hover:text-indigo-12'
                  "
                >
                  Présence d'un ascenseur
                </span>
                <span
                  class="text-xs transition-colors duration-200"
                  [class]="
                    model().accessibility.buildingHasElevator
                      ? 'text-indigo-11 dark:text-indigo-9'
                      : 'text-neutral-10'
                  "
                >
                  Ascenseur en service dans l'immeuble
                </span>
              </div>
            </div>

            <div
              class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-all duration-200"
              [class]="
                model().accessibility.buildingHasElevator
                  ? 'border-indigo-9 bg-indigo-9 text-white shadow-2xs'
                  : 'border-neutral-5 bg-neutral-2 group-hover:border-indigo-6'
              "
            >
              @if (model().accessibility.buildingHasElevator) {
                <svg
                  class="h-3.5 w-3.5 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              }
            </div>
          </button>

          <!-- Carte PMR -->
          <button
            type="button"
            (click)="
              model.update((m) => ({
                ...m,
                accessibility: {
                  ...m.accessibility,
                  pmrCompliance: !m.accessibility.pmrCompliance,
                },
              }))
            "
            class="group flex cursor-pointer items-center justify-between rounded-2xl border p-4 text-left transition-all duration-200 select-none"
            [class]="
              model().accessibility.pmrCompliance
                ? 'border-indigo-8 bg-indigo-a2 dark:bg-indigo-950/40 ring-2 ring-indigo-7/20 shadow-xs'
                : 'border-neutral-4 bg-neutral-1/90 hover:border-indigo-6/50 hover:bg-neutral-2/60'
            "
          >
            <div class="flex items-center gap-3.5">
              <div
                class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-all duration-200"
                [class]="
                  model().accessibility.pmrCompliance
                    ? 'bg-indigo-9 text-white shadow-sm shadow-indigo-9/30'
                    : 'bg-indigo-a3 text-indigo-11 border border-indigo-a4 group-hover:bg-indigo-a4 group-hover:text-indigo-12'
                "
              >
                <svg
                  class="h-6 w-6 shrink-0 transition-colors duration-200"
                  [class]="
                    model().accessibility.pmrCompliance
                      ? 'text-white'
                      : 'text-indigo-11 group-hover:text-indigo-12'
                  "
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="16" cy="4" r="1.5" />
                  <path d="m18 19 1-7-6 1" />
                  <path d="m5 8 3-3 5.5 3-2.36 3.5" />
                  <path d="M4.24 14.5a5 5 0 0 0 6.88 6" />
                  <path d="M13.76 17.5a5 5 0 0 0-6.88-6" />
                </svg>
              </div>
              <div class="flex flex-col gap-0.5">
                <span
                  class="text-sm font-semibold transition-colors duration-200"
                  [class]="
                    model().accessibility.pmrCompliance
                      ? 'text-indigo-12 dark:text-white'
                      : 'text-neutral-12 group-hover:text-indigo-12'
                  "
                >
                  Logement adapté PMR
                </span>
                <span
                  class="text-xs transition-colors duration-200"
                  [class]="
                    model().accessibility.pmrCompliance
                      ? 'text-indigo-11 dark:text-indigo-9'
                      : 'text-neutral-10'
                  "
                >
                  Accessibilité personnes à mobilité réduite
                </span>
              </div>
            </div>

            <div
              class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-all duration-200"
              [class]="
                model().accessibility.pmrCompliance
                  ? 'border-indigo-9 bg-indigo-9 text-white shadow-2xs'
                  : 'border-neutral-5 bg-neutral-2 group-hover:border-indigo-6'
              "
            >
              @if (model().accessibility.pmrCompliance) {
                <svg
                  class="h-3.5 w-3.5 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              }
            </div>
          </button>
        </div>
      }
    </div>
  </div>
</div>
